import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { loadProjectPictures } from "../../helpers/projectPictures";
import { setPicturesClientVisible } from "../../helpers/clientView/clientViewShare";
import { friendlyError } from "../../helpers/clientView/clientViewModel";
import { isOffline } from "../../helpers/connectivity";
import { RecBar, RecIcon } from "../materials/MaterialRecordParts";
import { ViewportPortal } from "../shared/ViewportDialog";
import "./clientViewManage.css";

const FILTERS = [
  ["all", "All"],
  ["visible", "Client Visible"],
  ["internal", "Internal Only"],
];

/**
 * Manage Pictures for Client View. Visibility is publication metadata on the
 * existing picture (nothing is copied). Manager/admin only; the database
 * enforces it as well.
 */
export function ClientPicturesManageScreen({ projectId, projectName, writable = true, onBack }) {
  const { actor } = usePermissions();
  const [pictures, setPictures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const offline = isOffline();

  const reload = useCallback(async () => {
    try {
      setPictures(await loadProjectPictures(actor, projectId));
      setError("");
    } catch (err) {
      setError(friendlyError(err, "Unable to load pictures."));
    } finally {
      setLoading(false);
    }
  }, [actor, projectId]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const counts = useMemo(() => {
    const visible = pictures.filter((p) => p.client_visible === true).length;
    return { all: pictures.length, visible, internal: pictures.length - visible };
  }, [pictures]);

  const shown = pictures.filter((p) =>
    filter === "visible" ? p.client_visible === true : filter === "internal" ? p.client_visible !== true : true,
  );
  const canChange = writable && !offline;

  function toggle(id) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function apply(ids, visible) {
    setBusy(true);
    setError("");
    try {
      await setPicturesClientVisible(actor, projectId, ids, visible);
      await reload();
      setSelected(new Set());
      setSelecting(false);
      setOpen((current) => (current ? { ...current, client_visible: visible } : current));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  const selectedRows = pictures.filter((p) => selected.has(p.picture_id));
  const anyInternal = selectedRows.some((p) => p.client_visible !== true);
  const anyVisible = selectedRows.some((p) => p.client_visible === true);

  return (
    <div className="screen nx-screen rec-page" data-testid="client-pictures-manage">
      <RecBar title="Client Pictures" onBack={onBack} />
      {projectName ? <div className="tk-subtitle">{projectName}</div> : null}

      <div className="rec-body">
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
        {offline ? <div className="tk-notice">Reconnect to change what your client can see.</div> : null}
        {!writable ? <div className="tk-notice">This project is archived, so pictures cannot be changed.</div> : null}

        <p className="cvm-help">
          Pictures are internal by default. Only the ones marked Client Visible appear in your client’s view.
        </p>

        <div className="tl-scope" role="tablist" aria-label="Picture visibility">
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={filter === value}
              className={`tl-scope__tab${filter === value ? " is-on" : ""}`}
              onClick={() => setFilter(value)}
            >
              {label} <span className="tk-count">{counts[value]}</span>
            </button>
          ))}
        </div>

        {canChange && pictures.length ? (
          selecting ? (
            <div className="mat-selbar cvm-selbar" role="toolbar" aria-label="Selection">
              <span className="mat-selbar__count" aria-live="polite">
                {selected.size} selected
              </span>
              <button
                type="button"
                className="mat-act mat-act--sm"
                disabled={busy}
                onClick={() =>
                  setSelected(selected.size === shown.length ? new Set() : new Set(shown.map((p) => p.picture_id)))
                }
              >
                {selected.size === shown.length && shown.length ? "Deselect All" : "Select All"}
              </button>
              <button
                type="button"
                className="mat-selbar__cancel"
                disabled={busy}
                onClick={() => {
                  setSelecting(false);
                  setSelected(new Set());
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="cvm-toolbar">
              <button type="button" className="mat-act" onClick={() => setSelecting(true)}>
                <RecIcon name="select" />
                <span>Select</span>
              </button>
            </div>
          )
        ) : null}

        {loading ? (
          <p className="pj-empty">Loading pictures…</p>
        ) : shown.length === 0 ? (
          <p className="pj-empty" data-testid="client-pictures-empty">
            {pictures.length === 0
              ? "No pictures on this project yet."
              : filter === "visible"
                ? "No pictures are shared with your client yet."
                : "No internal-only pictures."}
          </p>
        ) : (
          <div className="cvm-grid">
            {shown.map((picture) => {
              const visible = picture.client_visible === true;
              const isSelected = selected.has(picture.picture_id);
              return (
                <button
                  key={picture.picture_id}
                  type="button"
                  className={`cvm-tile${isSelected ? " is-selected" : ""}`}
                  role={selecting ? "checkbox" : undefined}
                  aria-checked={selecting ? isSelected : undefined}
                  aria-label={`${visible ? "Client visible" : "Internal only"} picture${picture.file_name ? ` ${picture.file_name}` : ""}`}
                  onClick={() => (selecting ? toggle(picture.picture_id) : setOpen(picture))}
                >
                  {picture.previewUrl ? <img src={picture.previewUrl} alt="" loading="lazy" /> : <span className="cvm-tile__ph" />}
                  <span className={`cvm-badge${visible ? " is-on" : ""}`}>{visible ? "Client" : "Internal"}</span>
                  {selecting ? (
                    <span className="cvm-check" aria-hidden="true">
                      {isSelected ? <RecIcon name="check" /> : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
        <div className="pj-pagepad" aria-hidden="true" />
      </div>

      {selecting && selected.size > 0 ? (
        <div className="rec-actions cvm-bulk">
          {anyInternal ? (
            <button
              type="button"
              className="rec-btn rec-btn--primary"
              disabled={busy}
              onClick={() => apply([...selected], true)}
            >
              Make Client Visible
            </button>
          ) : null}
          {anyVisible ? (
            <button
              type="button"
              className="rec-btn rec-btn--outline"
              disabled={busy}
              onClick={() => apply([...selected], false)}
            >
              Make Internal Only
            </button>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <ViewportPortal>
        <div className="rec-sheet-overlay" role="presentation" onClick={() => setOpen(null)}>
          <div
            className="rec-sheet cvm-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Picture"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="rec-sheet__handle" aria-hidden="true" />
            {open.previewUrl ? <img className="cvm-sheet__img" src={open.previewUrl} alt="" /> : null}
            <div className="cvm-sheet__meta">
              <span className={`tk-chip tk-chip--status${open.client_visible ? " is-done" : ""}`}>
                {open.client_visible ? "Client Visible" : "Internal Only"}
              </span>
            </div>
            {canChange ? (
              <button
                type="button"
                className={`rec-btn ${open.client_visible ? "rec-btn--outline" : "rec-btn--primary"}`}
                disabled={busy}
                onClick={() => apply([open.picture_id], !open.client_visible)}
              >
                {open.client_visible ? "Make Internal Only" : "Make Client Visible"}
              </button>
            ) : null}
            <button type="button" className="rec-sheet__cancel" onClick={() => setOpen(null)}>
              Close
            </button>
          </div>
        </div>
        </ViewportPortal>
      ) : null}
    </div>
  );
}
